import React, { useState } from 'react';
import { MailIcon, MapPinIcon, SendIcon } from 'lucide-react';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema } from '../utils/structuredData';
export function ContactPage() {
  usePageSEO({
    canonical: '/contact',
    keywords: 'contact Design Flow Digitals, hire Shopify developer, Shopify agency contact, ecommerce project inquiry',
    jsonLd: breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Contact',
      path: '/contact'
    }]),
    title: 'Contact | Design Flow Digitals',
    description: 'Get in touch with Design Flow Digitals to discuss your Shopify project. Custom store development, theme design, and app solutions for premium ecommerce brands.'
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    // Simulate API call
    setTimeout(() => setFormStatus('success'), 1500);
  };
  return <div className="pt-32 pb-20" data-id="element-919" data-mp="mzcskhj5ilqo">
      <section className="px-6 md:px-12 max-w-5xl mx-auto text-center mb-20" data-id="element-920" data-mp="n04qw044uxkn">
        <h1 className="text-5xl md:text-7xl font-bold mb-8" data-id="element-921" data-mp="hzgxx8wcg6ns">Contact Us</h1>
        <p className="text-xl md:text-2xl text-gray-400 leading-relaxed" data-id="element-922" data-mp="d0w0fh8h8131">
          Have a project in mind? We'd love to hear about it.
        </p>
      </section>

      <section className="section-padding pt-0" data-id="element-923" data-mp="83dqpommxhtz">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-6xl mx-auto" data-id="element-924" data-mp="3bcc3w1zvf0s">
          {/* Contact Info */}
          <div className="flex flex-col gap-12" data-id="element-925" data-mp="3ve3m2zja6ex3">
            <div className="glass-card p-10 border-white/10" data-id="element-926" data-mp="3vcsj31xntb71">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-6" data-id="element-927" data-mp="3qh3dohyyl02t">
                <MailIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-928" data-mp="3lg7ak6mflhyg" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-929" data-mp="3ginmyg3ufstu">Email</h3>
              <p className="text-gray-400 mb-4" data-id="element-930" data-mp="36ua0exv6o69w">
                Our friendly team is here to help.
              </p>
              <a href="mailto:support@designflowdigitals.com" className="text-xl font-medium text-gradient-gold hover:opacity-80 transition-opacity" data-id="element-931" data-mp="3btd16644rqc2">
                support@designflowdigitals.com
              </a>
            </div>

            <div className="glass-card p-10 border-white/10" data-id="element-932" data-mp="3pkrk6cy1frj4">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-6" data-id="element-933" data-mp="3pmkvw6nid1xz">
                <MapPinIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-934" data-mp="3ulfb6ym9o8dc" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-935" data-mp="3grxvxq0m5ht4">Address</h3>
              <p className="text-gray-400 mb-4" data-id="element-936" data-mp="3ksglteiy4dso">
                Come say hello at our office HQ.
              </p>
              <address className="text-lg text-white not-italic leading-relaxed" data-id="element-937" data-mp="ccojyzt6qc88">
                Design Flow Digitals
                <br data-id="element-938" data-mp="h6j3a24vop6s" />
                Pankaj Maurya LLC
                <br data-id="element-939" data-mp="21tsiyrhfw5d9" />
                67 Wigley Loop
                <br data-id="element-940" data-mp="1ww0e6wvp4orn" />
                Whitwell, TN, 37397
                <br data-id="element-941" data-mp="1wvh76b7l3kse" />
                United States
              </address>
            </div>
          </div>

          {/* Contact Form */}
          <div className="glass-card p-10 md:p-12 border-white/10" data-id="element-942" data-mp="26ltozvo0kzuu">
            <h3 className="text-2xl font-bold text-white mb-8" data-id="element-943" data-mp="26mllr9tcl3mp">
              Send us a message
            </h3>

            {formStatus === 'success' ? <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-8 text-center" data-id="element-944" data-mp="1dxxwz49e3w4e">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-945" data-mp="1suowremz79uj">
                  <SendIcon className="w-8 h-8 text-green-500" data-id="element-946" data-mp="1nuiz9hu0yt4j" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-947" data-mp="348j0qx8iqqtw">
                  Message Sent!
                </h4>
                <p className="text-gray-400" data-id="element-948" data-mp="2z9z9qif2iouo">
                  We'll get back to you as soon as possible.
                </p>
                <button onClick={() => setFormStatus('idle')} className="mt-6 text-[#D4AF37] hover:underline" data-id="element-949" data-mp="18vqd4o0sndd9">
                  Send another message
                </button>
              </div> : <form onSubmit={handleSubmit} className="flex flex-col gap-6" data-id="element-950" data-mp="1muq07r8nrk7b">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-951" data-mp="1rt16jrzqvcks">
                  <div className="flex flex-col gap-2" data-id="element-952" data-mp="1wr2ryjkn7nzf">
                    <label htmlFor="name" className="text-sm font-medium text-gray-300" data-id="element-953" data-mp="1wphxsv28q723">
                      Name
                    </label>
                    <input type="text" id="name" required className="bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors" placeholder="John Doe" data-id="element-954" data-mp="25lqwg7ie91n6" />
                  </div>
                  <div className="flex flex-col gap-2" data-id="element-955" data-mp="2klwgjhiu8ntm">
                    <label htmlFor="email" className="text-sm font-medium text-gray-300" data-id="element-956" data-mp="2fnbc15wjzhzd">
                      Email
                    </label>
                    <input type="email" id="email" required className="bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors" placeholder="john@example.com" data-id="element-957" data-mp="38cs0xtkysgqu" />
                  </div>
                </div>

                <div className="flex flex-col gap-2" data-id="element-958" data-mp="3r2zzdydpyj0p">
                  <label htmlFor="subject" className="text-sm font-medium text-gray-300" data-id="element-959" data-mp="3r4kvvtjlgc17">
                    Subject
                  </label>
                  <input type="text" id="subject" required className="bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors" placeholder="How can we help?" data-id="element-960" data-mp="1kgtzp1e3cmbg" />
                </div>

                <div className="flex flex-col gap-2" data-id="element-961" data-mp="11mf13cvoyvo5">
                  <label htmlFor="message" className="text-sm font-medium text-gray-300" data-id="element-962" data-mp="16nm6x6e9xks6">
                    Message
                  </label>
                  <textarea id="message" rows={5} required className="bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors resize-none" placeholder="Tell us about your project..." data-id="element-963" data-mp="wodggnimaoak" />
                </div>

                <button type="submit" disabled={formStatus === 'submitting'} className="bg-gradient-gold text-black px-8 py-4 rounded-lg font-bold text-lg gold-glow-hover transition-all duration-300 mt-4 disabled:opacity-70 flex items-center justify-center gap-2" data-id="element-964" data-mp="agzw6chavida">
                  {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>
              </form>}
          </div>
        </div>
      </section>
    </div>;
}